import { Form } from "antd";
import { useStore } from "zustand";
import { useDashboardName } from "../../store/headerName";
import { useEffect, useRef } from "react";
import CustomBreadcrumb from "../../components/CustomBreadcrumb";
import FormCard from "../../components/card/FormCard";
import FormItemInput from "../../components/form-item/form-item-input";
import FormItemInputSelect from "../../components/form-item/form-item-input-select";
import FormItemInputTextarea from "../../components/form-item/form-item-input-textarea";
import FormButton from "../../components/FormButton";
import { $api } from "../../helper";
import { repeatMock, statusShowMock, tokenMock } from "../../helper/mock";
import { useNavigate, useSearchParams } from "react-router";
import { useNotify } from "../../helper/notify";
import Quill from "quill";

export default function AddFaq() {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const quillRef = useRef<Quill | null>(null);
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const notify = useNotify();
  const detail = $api.useMutation("get", "/site-api/v1/faq-detail/");
  const catList = $api.useQuery("get", "/site-api/v1/faq/category/", {
    headers: {
      Authorization: tokenMock,
      lang: "fa",
    },
  });
  const { mutate, isPending } = $api.useMutation("post", "/site-api/v1/faq/");
  const update = $api.useMutation("put", "/site-api/v1/faq/");

  const { setDashboardName } = useStore(useDashboardName);
  useEffect(() => {
    setDashboardName("ایجاد سوال جدید");
    if (searchParams.get("u_id")) {
      detail.mutate({
        params: {
          query: {
            faq_u_id: searchParams.get("u_id") ?? "",
          },
        },
        headers: {
          Authorization: tokenMock,
          lang: "fa",
        },
      });
    }
  }, []);

  useEffect(() => {
    if (!editorRef.current) return;

    if (quillRef.current) return;

    quillRef.current = new Quill(editorRef.current, {
      theme: "snow",
      modules: {
        toolbar: [
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          ["bold", "italic", "underline", "strike"],
          [{ color: [] }, { background: [] }],
          [{ direction: "rtl" }],
          [{ align: [] }],
          ["link"],
          ["clean"],
        ],
      },
    });
  }, []);

  useEffect(() => {
    if (searchParams.get("u_id") && detail.data) {
      Object.entries(detail.data?.data)?.forEach(([key, value]) => {
        form.setFieldValue(key, value.toString());
      });
      quillRef.current?.clipboard.dangerouslyPasteHTML(detail.data.data.text);
    }
  }, [detail.data]);

  useEffect(() => {
    if (quillRef.current) {
      quillRef.current.format("align", "right");
      quillRef.current.on("text-change", () => {
        form.setFieldValue(
          "text",
          Number(quillRef.current?.editor.delta.ops[0].insert?.length) <= 1
            ? ""
            : quillRef?.current && quillRef?.current?.root.innerHTML,
        );
      });
    }
  }, [quillRef.current?.editor.getText]);

  const onFinish = (
    values: {
      title: string;
      category_u_id: string;
      status: string;
      text: string;
      seo_title: string;
      seo_description: string;
      is_repeat: string;
    },
    redirect = true,
  ) => {
    if (searchParams.get("u_id")) {
      update.mutate(
        {
          body: {
            ...values,
            status: +values.status,
            is_repeat: JSON.parse(values.is_repeat),
            faq_u_id: searchParams.get("u_id") ?? "",
          },
          headers: {
            Authorization: tokenMock,
            lang: "fa",
          },
        },
        {
          onSuccess: (data) => {
            notify.success(data.message);
            if (!redirect) {
              setTimeout(() => {
                window.location.href = window.location.pathname;
              }, 1000);
            } else {
              navigate("/faq");
            }
          },
        },
      );
    } else {
      mutate(
        {
          body: {
            ...values,
            status: +values.status,
            is_repeat: JSON.parse(values.is_repeat),
          },
          headers: {
            "Content-Type": "application/json",
            Authorization: tokenMock,
            lang: "fa",
          },
        },
        {
          onSuccess: (data) => {
            notify.success(data.message);
            if (!redirect) {
              setTimeout(() => {
                window.location.href = window.location.pathname;
              }, 1000);
            } else {
              navigate("/faq");
            }
          },
        },
      );
    }
  };

  return (
    <Form
      form={form}
      onFinish={(e) => onFinish(e)}
      layout="vertical"
      className="flex! flex-col! gap-6!"
    >
      <CustomBreadcrumb
        items={[
          {
            title: "سوالات متداول",
            href: "/faq",
          },
          {
            title: "ایجاد سوال جدید",
          },
        ]}
      />
      <FormCard
        subTitle="تعریف و تنظیمات پایه مربوط به سوال"
        title="اطلاعات اصلی سوال"
      >
        <div className="grid sm:grid-cols-2 grid-cols-1 w-full gap-6">
          <FormItemInput
            name={"title"}
            rules={[
              {
                required: true,
                message: "عنوان سوال اجباری است",
              },
            ]}
            className="col-span-1"
            label="عنوان سوال"
            inputProps={{
              placeholder: "لطفا عنوان سوال خود را وارد کنید.",
            }}
          />
          <FormItemInputSelect
            className="col-span-1"
            label="دسته بندی سوال"
            name={"category_u_id"}
            rules={[
              {
                required: true,
                message: "دسته بندی سوال اجباری است",
              },
            ]}
            inputProps={{
              placeholder: "لطفا دسته بندی سوال خود را انتخاب کنید.",
              options: catList.data?.data?.map((e) => ({
                value: e.u_id,
                label: e.title,
              })),
            }}
          />
          <FormItemInputSelect
            className="col-span-1"
            label="وضعیت"
            name={"status"}
            rules={[
              {
                required: true,
                message: "وضعیت اجباری است",
              },
            ]}
            inputProps={{
              placeholder: "لطفا وضعیت نمایش سوال خود را انتخاب کنید.",
              options: statusShowMock,
            }}
          />
          <FormItemInputSelect
            className="col-span-1"
            label="سوال پر تکرار است؟"
            name={"is_repeat"}
            rules={[
              {
                required: true,
                message: "وضعیت اجباری است",
              },
            ]}
            inputProps={{
              placeholder: "لطفا وضعیت نمایش سوال خود را انتخاب کنید.",
              options: repeatMock,
            }}
          />
        </div>
      </FormCard>
      <FormCard subTitle="متن کامل سوال" title="متن">
        <div className="grid grid-cols-2 w-full gap-6">
          <div className="col-span-2">
            <Form.Item
              name={"text"}
              rules={[
                {
                  required: true,
                  message: "توضیحات اجباری است",
                },
              ]}
              label="توضیحات کامل"
            >
              <div
                dir="auto"
                className="w-full border-r border-l border-b border-grayscale-200 h-96! rounded-br-xl rounded-bl-xl"
                lang="fa"
                ref={editorRef}
              />
            </Form.Item>
          </div>
        </div>
      </FormCard>
      <FormCard
        title="اطلاعات سئو"
        subTitle="بهینه‌سازی سوال برای موتورهای جستجو"
      >
        <div className="grid sm:grid-cols-2 grid-cols-1 w-full gap-6">
          <FormItemInput
            rules={[
              {
                required: true,
                message: "عنوان سئو اجباری است",
              },
            ]}
            label="عنوان سئو"
            name={"seo_title"}
            className="col-span-1"
            inputProps={{
              placeholder: "لطفا عنوان سئو خود را وارد کنید.",
            }}
          />
          <FormItemInputTextarea
            label="توضیحات"
            className="col-span-1"
            name={"seo_description"}
            rules={[
              {
                required: true,
                message: "توضیحات سئو اجباری است",
              },
            ]}
            inputProps={{
              className: "resize-none!",
              rows: 4,
              placeholder: "لطفا توضیحات اضافی سئو خود را بنویسید...",
            }}
          />
        </div>
      </FormCard>
      <FormButton
        isPending={isPending || update.isPending}
        ActionSaveAndStay={() => {
          form.validateFields().then(() => {
            onFinish(form.getFieldsValue(), false);
          });
        }}
      />
    </Form>
  );
}
